import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import api from '../services/api';

// Real-time delivery transfers, polled every 5 s. The `sourceType` prop
// scopes the panel to a single product surface ("dcp" on the DCP Library,
// "screener" on the Watermark Library) so the panel doesn't show transfers
// from the other product. NAS read / outbound throughput numbers are
// derived from delta'd /proc/net/dev counters on the delivery node.
export default function ActiveTransfersPanel({ sourceType }) {
  const [transfers, setTransfers] = useState([]);
  const [nodeIO, setNodeIO] = useState(null);
  const prevStats = useRef(null);
  const prevTime = useRef(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get('/api/delivery/active-transfers', {
          params: sourceType ? { source_type: sourceType } : undefined,
        });
        if (!alive) return;
        setTransfers(data.transfers || []);
        const now = Date.now();
        const stats = data.node_stats;
        if (stats && prevStats.current && prevTime.current) {
          const dt = (now - prevTime.current) / 1000;
          if (dt > 0) {
            const txDelta = (stats.ens18_tx_bytes || 0) - (prevStats.current.ens18_tx_bytes || 0);
            const rxDelta = (stats.ens19_rx_bytes || 0) - (prevStats.current.ens19_rx_bytes || 0);
            setNodeIO({
              outbound: Math.round(txDelta * 8 / dt / 1_000_000),
              nas_read: Math.round(rxDelta * 8 / dt / 1_000_000),
            });
          }
        }
        prevStats.current = stats;
        prevTime.current = now;
      } catch (_) { /* swallow */ }
    };
    tick();
    const t = setInterval(() => { if (!document.hidden) tick(); }, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [sourceType]);

  // Render-suppression: hide entirely when there are no user-facing
  // transfers. Internal MinIO sync traffic (NAS Read activity) shouldn't
  // surface a "0 active transfers" panel — that reads as broken state.
  // (Includes both live-TCP and download-event-derived in-flight rows.)
  if (transfers.length === 0) {
    return null;
  }
  const fmtAgo = (iso) => {
    if (!iso) return '';
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ${m % 60}m ago`;
  };

  const fmtEta = (s) => {
    if (s == null) return '—';
    if (s > 3600) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
    if (s > 60) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${s}s`;
  };

  return (
    <Paper sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'info.main', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main',
            '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
            animation: 'pulse 2s ease-in-out infinite',
          }} />
          Active Transfers ({transfers.length})
        </Typography>
        <Box sx={{ flex: 1 }} />
        {nodeIO && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Outbound: <strong>{nodeIO.outbound} Mbps</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              NAS Read: <strong>{nodeIO.nas_read} Mbps</strong>
            </Typography>
          </Box>
        )}
      </Box>
      {transfers.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 0.5 }}>Recipient</TableCell>
              <TableCell sx={{ py: 0.5 }}>Project</TableCell>
              <TableCell sx={{ py: 0.5 }}>Progress</TableCell>
              <TableCell sx={{ py: 0.5 }}>Speed</TableCell>
              <TableCell sx={{ py: 0.5 }}>ETA</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transfers.map((t, idx) => (
              <TableRow key={idx} sx={{ opacity: t.live === false ? 0.7 : 1 }}>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption">{t.recipient || t.client_ip}</Typography>
                  {t.live === false && (
                    <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary' }}>
                      started {fmtAgo(t.started_at)} · between bursts
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption">{t.project || '—'}</Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flexGrow: 1, height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
                      <Box sx={{
                        height: '100%',
                        width: `${Math.min(t.progress ?? 0, 100)}%`,
                        bgcolor: t.live === false ? 'warning.main' : 'info.main',
                        borderRadius: 3, transition: 'width 0.5s',
                      }} />
                    </Box>
                    <Typography variant="caption" sx={{ minWidth: 45 }}>
                      {t.progress != null ? `${t.progress}%` : '—'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption">{t.rate_mbps ? `${t.rate_mbps} Mbps` : '—'}</Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption">{fmtEta(t.eta_seconds)}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
